import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    useMediaQuery,
    useTheme,
    Box,
    Typography,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Card,
    CardContent,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BarChartIcon from '@mui/icons-material/BarChart';

// Import the RecordEditModal component
import RecordEditModal from './RecordEditModal';

const RecordModal = ({ open, onClose, recordType, recordName }) => {
    const [allRecords, setAllRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewEditRecord, setViewEditRecord] = useState(null);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [expandedRowId, setExpandedRowId] = useState(null);

    // Responsive design
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);

    // Load all records for this name
    const loadAllRecords = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(
                `${baseUrl}/records/${encodeURIComponent(recordName)}?type=${recordType}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            if (!response.ok) {
                throw new Error('Error fetching records by name');
            }
            const data = await response.json();
            setAllRecords(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Load data when modal opens
    useEffect(() => {
        if (open && recordName) {
            loadAllRecords();
        }
        // eslint-disable-next-line
    }, [open, recordName]);

    // Handle record deletion - updated to use Dialog
    const handleDeleteRecord = (recordId) => {
        setRecordToDelete(recordId);
        setDeleteConfirmOpen(true);
    };

    // Handle confirmed deletion
    const handleConfirmDeleteRecord = async () => {
        try {
            if (!recordToDelete) return;

            const token = localStorage.getItem('token');
            const baseUrl = process.env.REACT_APP_API_URL;
            const response = await fetch(`${baseUrl}/records/${recordToDelete}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) {
                throw new Error('Failed to delete record');
            }
            loadAllRecords();
            setDeleteConfirmOpen(false);
            setRecordToDelete(null);
        } catch (err) {
            console.error(err);
            setDeleteConfirmOpen(false);
        }
    };

    // Open edit dialog for a record
    const handleEditRecord = (record) => {
        console.log('record', record);
        setViewEditRecord(record);
        setShowEditDialog(true);
    };

    // Format full date for table view
    const formatFullDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const toggleRowExpand = (recordId) => {
        if (isMobile) {
            setExpandedRowId(prevId => prevId === recordId ? null : recordId);
        }
    };

    // Prepare data for calculations
    const chartData = useMemo(() => {
        if (!allRecords || allRecords.length === 0) return [];

        // Sort by date (oldest to newest)
        const sorted = [...allRecords].sort((a, b) => new Date(a.date) - new Date(b.date));

        return sorted.map((r) => {
            let rawValue = '';
            if (recordType === 'Cardio') {
                rawValue = r.time || '';
            } else if (recordType === 'Weightlifting') {
                rawValue = r.weight != null ? String(r.weight) : '';
            } else {
                // WOD
                rawValue = r.score || '';
            }

            // Convert time format for calculations
            const parseString = rawValue.includes(':')
                ? rawValue.replace(':', '.')
                : rawValue;

            const floatVal = parseFloat(parseString) || 0;

            return {
                id: r.id,
                value: floatVal,
                displayValue: rawValue,
                fullDate: r.date,
                comment: r.comment,
                hasColon: rawValue.includes(':')
            };
        });
    }, [allRecords, recordType]);

    // Calculate statistics
    const stats = useMemo(() => {
        if (!chartData || chartData.length === 0) {
            return {
                bestValue: 0,
                bestDate: '',
                worstValue: 0,
                worstDate: '',
                avgValue: 0,
                totalEntries: 0,
                recentTrend: 0,
                progress: 0
            };
        }

        let bestRecord, worstRecord;

        // Check if any records have colon (time format)
        const hasTimeFormat = chartData.some(record => record.hasColon);

        if (hasTimeFormat) {
            // For time format, lower is better
            bestRecord = [...chartData].sort((a, b) => a.value - b.value)[0];
            worstRecord = [...chartData].sort((a, b) => b.value - a.value)[0];
        } else {
            // For numeric scores, higher is better
            bestRecord = [...chartData].sort((a, b) => b.value - a.value)[0];
            worstRecord = [...chartData].sort((a, b) => a.value - b.value)[0];
        }

        // Calculate average
        const sum = chartData.reduce((acc, record) => acc + record.value, 0);
        const avg = sum / chartData.length;

        // Calculate recent trend (last 2 records)
        const sortedByDate = [...chartData].sort((a, b) =>
            new Date(b.fullDate) - new Date(a.fullDate));

        let recentTrend = 0;
        let progress = 0;

        if (sortedByDate.length >= 2) {
            const latest = sortedByDate[0].value;
            const previous = sortedByDate[1].value;

            if (hasTimeFormat) {
                // For time format, negative percentage is good (faster time)
                recentTrend = ((latest - previous) / previous) * 100;

                // Overall progress (first record vs latest record)
                const first = chartData[0].value;
                progress = ((latest - first) / first) * 100;
            } else {
                // For numeric scores, positive percentage is good
                recentTrend = ((latest - previous) / previous) * 100;

                // Overall progress
                const first = chartData[0].value;
                progress = ((latest - first) / first) * 100;
            }
        }

        return {
            bestValue: bestRecord?.displayValue || 0,
            bestDate: formatFullDate(bestRecord?.fullDate) || '',
            worstValue: worstRecord?.displayValue || 0,
            worstDate: formatFullDate(worstRecord?.fullDate) || '',
            avgValue: avg.toFixed(2),
            totalEntries: chartData.length,
            recentTrend: recentTrend.toFixed(1),
            progress: progress.toFixed(1),
            hasTimeFormat
        };
    }, [chartData]);

    // Determine if trend is positive or negative based on data format
    const isTrendPositive = (value) => {
        if (stats.hasTimeFormat) {
            // For time format, negative trend is good (faster time)
            return parseFloat(value) < 0;
        }
        // For numeric scores, positive trend is good
        return parseFloat(value) > 0;
    };

    // Get trend color based on value and data format
    const getTrendColor = (value) => {
        const isPositive = isTrendPositive(value);
        return isPositive ? theme.palette.success.main : theme.palette.error.main;
    };

    // Get trend icon based on value and data format
    const getTrendIcon = (value) => {
        const isPositive = isTrendPositive(value);
        return isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />;
    };

    // Render stat card with icon, title, and value
    const StatCard = ({ icon, title, value, color, secondary }) => (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {icon}
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        {title}
                    </Typography>
                </Box>
                <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color }}>
                    {value}
                </Typography>
                {secondary && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {secondary}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullScreen={isMobile}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                            {recordName}
                            <Typography component="span" variant="subtitle1" sx={{ ml: 1, color: 'text.secondary' }}>
                                ({recordType})
                            </Typography>
                        </Typography>
                        <IconButton
                            aria-label="close"
                            onClick={onClose}
                            sx={{ ml: 'auto' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : chartData.length === 0 ? (
                        <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
                            No data found for this record.
                        </Typography>
                    ) : (
                        <>
                            {/* Stats summary section */}
                            <Box sx={{ mb: 4 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <StatCard
                                            icon={<EmojiEventsIcon color="primary" />}
                                            title="Personal Best"
                                            value={stats.bestValue}
                                            color={theme.palette.primary.main}
                                            secondary={`Achieved on ${stats.bestDate}`}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <StatCard
                                            icon={getTrendIcon(stats.recentTrend)}
                                            title="Recent Trend"
                                            value={`${stats.recentTrend}%`}
                                            color={getTrendColor(stats.recentTrend)}
                                            secondary="Last record vs previous"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <StatCard
                                            icon={getTrendIcon(stats.progress)}
                                            title="Overall Progress"
                                            value={`${stats.progress}%`}
                                            color={getTrendColor(stats.progress)}
                                            secondary="First record vs latest"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <StatCard
                                            icon={<BarChartIcon />}
                                            title="Average"
                                            value={stats.avgValue}
                                            secondary={`From ${stats.totalEntries} entries`}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Data Table */}
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>
                                                {recordType === 'Weightlifting' ? 'Weight (kg)' :
                                                    recordType === 'Cardio' ? 'Time' : 'Score'}
                                            </TableCell>
                                            <TableCell>Change</TableCell>
                                            {!isMobile && (
                                                <TableCell>Comment</TableCell>
                                            )}
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {chartData.sort((a, b) => new Date(b.fullDate) - new Date(a.fullDate)).map((record, idx, arr) => {
                                            // Calculate change from previous record (if exists)
                                            let change = null;
                                            if (idx < arr.length - 1) {
                                                const prevValue = arr[idx + 1].value;
                                                const currValue = record.value;
                                                const diff = ((currValue - prevValue) / prevValue) * 100;
                                                change = diff.toFixed(1);
                                            }

                                            return (
                                                <React.Fragment key={record.id}>
                                                    <TableRow
                                                        hover
                                                        onClick={() => toggleRowExpand(record.id)}
                                                        sx={{
                                                            cursor: isMobile ? 'pointer' : 'default',
                                                            '& > *': { borderBottom: expandedRowId === record.id ? 0 : undefined }
                                                        }}
                                                    >
                                                        <TableCell>{formatFullDate(record.fullDate)}</TableCell>
                                                        <TableCell>
                                                            {(() => {
                                                                let display = record.displayValue;
                                                                // If it's a WOD record and has scoreType, add it in parentheses
                                                                if (recordType === 'WOD' && allRecords.find(r => r.id === record.id)?.scoreType) {
                                                                    const originalRecord = allRecords.find(r => r.id === record.id);
                                                                    const scoreOnly = originalRecord.score || '';
                                                                    display = `${scoreOnly} (${originalRecord.scoreType})`;
                                                                }
                                                                return display;
                                                            })()}
                                                        </TableCell>
                                                        <TableCell>
                                                            {change !== null && (
                                                                <Chip
                                                                    size="small"
                                                                    label={`${change > 0 ? '+' : ''}${change}%`}
                                                                    color={
                                                                        isTrendPositive(change)
                                                                            ? 'success'
                                                                            : 'error'
                                                                    }
                                                                    icon={getTrendIcon(change)}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        {!isMobile && (
                                                            <TableCell>
                                                                {record.comment && (
                                                                    <Typography variant="body2" sx={{
                                                                        maxWidth: 200,
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {record.comment}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                        )}
                                                        <TableCell align="right">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditRecord(record);
                                                                }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteRecord(record.id);
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>

                                                    {/* Comment row for mobile view */}
                                                    {isMobile && expandedRowId === record.id && record.comment && (
                                                        <TableRow sx={{ backgroundColor: theme.palette.primary.light + '0D' }}>
                                                            <TableCell colSpan={5} sx={{ py: 1.5, px: 2 }}>
                                                                <Box>
                                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                                        Comment:
                                                                    </Typography>
                                                                    <Typography variant="body2">
                                                                        {record.comment}
                                                                    </Typography>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose} variant="outlined">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this record? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDeleteRecord} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Record Modal */}
            {showEditDialog && viewEditRecord && (
                <RecordEditModal
                    open={showEditDialog}
                    onClose={() => setShowEditDialog(false)}
                    record={viewEditRecord}
                    recordType={recordType}
                    onSave={loadAllRecords}
                />
            )}
        </>
    );
};

export default RecordModal;